import {
  CHAMBER_HEIGHT,
  GOLD_PATENT_CHAMBERS,
  PULP_RADIUS,
  REGULAR_PATENT_CHAMBERS,
  type ChamberSpec,
  type EnemyMotion,
  type EraSpec,
  type HazardSpec,
  type Material,
  type PowerupSpec,
  type SegmentSpec,
  WALL_LEFT,
  WALL_RIGHT,
} from "./constants";
import { ENEMY_MOTIONS, eraForChamber, eraTransitionAt } from "./eras";
import { hashChoice } from "./random";
import { ENEMY_SPECS, enemyIdsForWeakness, type EnemySpriteId } from "./enemies";

type ChamberLayout = Omit<ChamberSpec, "era">;

/**
 * Flow invariants. Every generated chamber must satisfy these or the tomato can wedge:
 * - Each rail is anchored behind exactly one chute wall, so no wall lane stays open.
 * - Each rail's slope ratio is at least MIN_SLOPE, well above surface friction.
 * - Overlapping rails keep at least MIN_RAIL_CLEARANCE of vertical space.
 * - Only upper rails move, and their travel is outward from the chute so their wall overlap is preserved.
 */
const MIN_SLOPE = 0.26;
const MAX_SLOPE = 0.5;
const MIN_RAIL_CLEARANCE = PULP_RADIUS * 2 + 16;
const BAND_BOTTOM_MARGIN = 40;
const THROAT_HALF_WIDTH = 21;
const SIEVE_DROP = 40;
const DEPARTURE_SPEED = 180;
const ENEMY_DROP = 32;
const RAIL_HALF_THICKNESS = 5;

function unit(seed: number, chamber: number, stream: number): number {
  return hashChoice(seed, chamber, stream) / 0x1_0000_0000;
}

function range(seed: number, chamber: number, stream: number, low: number, high: number): number {
  return low + unit(seed, chamber, stream) * (high - low);
}

function pick(seed: number, chamber: number, stream: number, count: number): number {
  return hashChoice(seed, chamber, stream) % count;
}

function lerp(from: number, to: number, progress: number): number {
  return from + (to - from) * progress;
}

function segment(chamber: number, suffix: string, ax: number, ay: number, bx: number, by: number): SegmentSpec {
  let left = ax;
  let right = bx;
  if (!(ax === bx && (ax === WALL_LEFT || ax === WALL_RIGHT))) {
    if (left === WALL_LEFT) left -= 16;
    else if (left === WALL_RIGHT) left += 16;
    if (right === WALL_LEFT) right -= 16;
    else if (right === WALL_RIGHT) right += 16;
  }
  return { id: `${chamber}:${suffix}`, chamber, ax: left, ay, bx: right, by };
}

interface HazardOptions {
  phase: number;
  motion: EnemyMotion;
  travelX?: number;
  travelY?: number;
  period?: number;
  reactivity?: number;
}

function hazard(
  chamber: number,
  suffix: string,
  kind: HazardSpec["kind"],
  weakness: Material,
  sprite: EnemySpriteId,
  x: number,
  y: number,
  width: number,
  height: number,
  options: HazardOptions,
): HazardSpec {
  return { id: `${chamber}:${suffix}`, chamber, kind, weakness, sprite, x, y, width, height, ...options };
}

function walls(index: number, base: number): SegmentSpec[] {
  return [
    segment(index, "wall-l", WALL_LEFT, base, WALL_LEFT, base + CHAMBER_HEIGHT),
    segment(index, "wall-r", WALL_RIGHT, base, WALL_RIGHT, base + CHAMBER_HEIGHT),
  ];
}

interface Rail {
  spec: SegmentSpec;
  fromRight: boolean;
  wallY: number;
  freeX: number;
  freeY: number;
}

function buildRail(
  seed: number,
  index: number,
  suffix: string,
  stream: number,
  fromRight: boolean,
  topLow: number,
  topHigh: number,
  freeCap: number,
  chamber: number,
): Rail {
  const wallX = fromRight ? WALL_RIGHT : WALL_LEFT;
  const wallY = Math.min(topHigh, Math.max(topLow, range(seed, index, stream, topLow, topHigh)));
  const slope = range(seed, index, stream + 1, MIN_SLOPE, MAX_SLOPE);
  const reach = range(seed, index, stream + 2, 96, 240);
  const span = Math.max(70, Math.min(reach, (freeCap - wallY) / slope));
  const freeX = fromRight ? wallX - span : wallX + span;
  const freeY = Math.min(freeCap, wallY + span * slope);

  return {
    spec: segment(chamber, suffix, wallX, wallY, freeX, freeY),
    fromRight,
    wallY,
    freeX,
    freeY,
  };
}

function cascade(seed: number, index: number, base: number, era: EraSpec): Rail[] {
  const firstFromRight = pick(seed, index, 10, 2) === 1;
  const spacingOffset = range(seed, index, 13, -era.spacingJitter, era.spacingJitter);
  const firstTop = base + range(seed, index, 14, 40, 72) + spacingOffset;
  const firstCap = base + range(seed, index, 15, 104, 132) + spacingOffset * 0.35;
  const first = buildRail(seed, index, "rail-a", 11, firstFromRight, firstTop, firstTop + range(seed, index, 16, 10, 34), firstCap, index);
  const secondTop = Math.max(first.freeY + MIN_RAIL_CLEARANCE, base + range(seed, index, 24, 138, 164) - spacingOffset * 0.25);
  const second = buildRail(seed, index, "rail-b", 21, !firstFromRight, secondTop, Math.max(secondTop, Math.min(base + 184, secondTop + range(seed, index, 25, 8, 26))), base + CHAMBER_HEIGHT - BAND_BOTTOM_MARGIN, index);

  const transition = eraTransitionAt(seed, base + CHAMBER_HEIGHT - 40);
  const movingChance = lerp(transition.era.movingPlatformChance, transition.nextEra.movingPlatformChance, transition.progress);
  if (index >= 2 && unit(seed, index, 70) < movingChance) {
    const travelMin = lerp(transition.era.platformTravel[0], transition.nextEra.platformTravel[0], transition.progress);
    const travelMax = lerp(transition.era.platformTravel[1], transition.nextEra.platformTravel[1], transition.progress);
    first.spec.phase = pick(seed, index, 71, 360);
    first.spec.travelX = (firstFromRight ? 1 : -1) * range(seed, index, 72, travelMin, travelMax);
    first.spec.period = lerp(transition.era.platformPeriod, transition.nextEra.platformPeriod, transition.progress);
  }

  return [first, second];
}

function powerupsFor(seed: number, index: number, base: number): PowerupSpec[] {
  if (index < 4) return [];
  const powerups: PowerupSpec[] = [];
  if (pick(seed, index, 83, REGULAR_PATENT_CHAMBERS) === 0) {
    powerups.push({ id: `${index}:regular-patent`, chamber: index, kind: "regular-patent", x: range(seed, index, 84, 112, 248), y: range(seed, index, 85, base + 68, base + 120), radius: 17 });
  }
  if (pick(seed, index, 80, GOLD_PATENT_CHAMBERS) === 0) {
    const yLow = powerups.length > 0 ? base + 154 : base + 72;
    powerups.push({ id: `${index}:gold-patent`, chamber: index, kind: "gold-patent", x: range(seed, index, 81, 112, 248), y: range(seed, index, 82, yLow, base + 184), radius: 18 });
  }
  return powerups;
}

function sieveChamber(seed: number, index: number, base: number, era: EraSpec): ChamberLayout {
  const spacingOffset = range(seed, index, 29, -era.spacingJitter, era.spacingJitter);
  const throatX = range(seed, index, 30, 122, 238);
  // Keep wall ends inside the 40-unit top margin even at minimum era jitter.
  const railY = Math.max(base + 40 + SIEVE_DROP, range(seed, index, 31, base + 78, base + 140) + spacingOffset);
  const exitFromRight = pick(seed, index, 32, 2) === 1;
  const exitTop = railY + MIN_RAIL_CLEARANCE;
  const exit = buildRail(seed, index, "sieve-exit", 33, exitFromRight, exitTop, Math.max(exitTop, Math.min(base + 202, exitTop + 22)), base + CHAMBER_HEIGHT - BAND_BOTTOM_MARGIN, index);
  return {
    index,
    label: "TIME SIEVE",
    segments: [
      ...walls(index, base),
      segment(index, "sieve-l", WALL_LEFT, railY - SIEVE_DROP, throatX - THROAT_HALF_WIDTH, railY),
      segment(index, "sieve-r", WALL_RIGHT, railY - SIEVE_DROP, throatX + THROAT_HALF_WIDTH, railY),
      exit.spec,
    ],
    hazards: [],
    powerups: powerupsFor(seed, index, base),
  };
}

function weightedMotion(seed: number, eraSeed: number, index: number, stream: number, y: number): EnemyMotion {
  const transition = eraTransitionAt(eraSeed, y);
  const roll = unit(seed, index, stream);
  let cumulative = 0;
  for (let motionIndex = 0; motionIndex < ENEMY_MOTIONS.length; motionIndex += 1) {
    const weight = lerp(transition.era.enemyMotionWeights[motionIndex]!, transition.nextEra.enemyMotionWeights[motionIndex]!, transition.progress);
    cumulative += weight;
    if (roll <= cumulative) return ENEMY_MOTIONS[motionIndex]!;
  }
  return "static";
}

function weaknessAt(seed: number, eraSeed: number, index: number, stream: number, y: number): Material {
  const transition = eraTransitionAt(eraSeed, y);
  const produceWeight = lerp(transition.era.produceWeight, transition.nextEra.produceWeight, transition.progress);
  return unit(seed, index, stream) < produceWeight ? "core" : "pulp";
}

function motionOptions(seed: number, eraSeed: number, index: number, stream: number, y: number): HazardOptions {
  const transition = eraTransitionAt(eraSeed, y);
  const motion = weightedMotion(seed, eraSeed, index, stream, y);
  const pace = lerp(transition.era.motionPace, transition.nextEra.motionPace, transition.progress);
  const phase = pick(seed, index, stream + 1, 360);
  if (motion === "horizontal") return { motion, phase, travelX: range(seed, index, stream + 2, 48, 92), period: 220 / pace };
  if (motion === "vertical") return { motion, phase, travelY: range(seed, index, stream + 2, 22, 42), period: 240 / pace };
  if (motion === "burst") return { motion, phase, travelX: range(seed, index, stream + 2, 58, 104), period: 190 / pace };
  if (motion === "adaptive") return { motion, phase, travelX: 28, travelY: 22, period: 230 / pace, reactivity: 0.14 };
  return { motion, phase };
}

function createEnemy(seed: number, eraSeed: number, index: number, suffix: string, stream: number, kind: HazardSpec["kind"], x: number, y: number): HazardSpec {
  const weakness = index === 3 && suffix === "enemy-a" ? "core" : weaknessAt(seed, eraSeed, index, stream, y);
  const spriteIds = enemyIdsForWeakness(weakness);
  const sprite = spriteIds[pick(seed, index, stream + 1, spriteIds.length)]!;
  const enemy = ENEMY_SPECS[sprite];
  const options = motionOptions(seed, eraSeed, index, stream + 2, y);
  return hazard(index, suffix, kind, weakness, sprite, x, y, enemy.collisionWidth, enemy.collisionHeight,
    index === 3 && suffix === "enemy-a" ? { motion: "static", phase: options.phase } : options);
}

interface Box { left: number; right: number; top: number; bottom: number }

/** Full runtime range, including burst asymmetry and both adaptive tracking terms. */
function enemyEnvelope(enemy: HazardSpec): Box {
  const tx = enemy.travelX ?? 0;
  const ty = enemy.travelY ?? 0;
  const xMin = enemy.motion === "burst" ? -tx * 0.25 : enemy.motion === "adaptive" ? -tx : enemy.motion === "horizontal" ? -tx / 2 : 0;
  const xMax = enemy.motion === "burst" ? tx * 0.75 : -xMin;
  const yRange = enemy.motion === "vertical" || enemy.motion === "adaptive" ? ty / 2 : 0;
  return {
    left: Math.max(WALL_LEFT + 4, enemy.x + xMin),
    right: Math.min(WALL_RIGHT - 4, enemy.x + enemy.width + xMax),
    top: enemy.y - yRange, bottom: enemy.y + enemy.height + yRange,
  };
}

function boxesOverlap(a: Box, b: Box): boolean {
  return a.left <= b.right && a.right >= b.left && a.top <= b.bottom && a.bottom >= b.top;
}

/** Slab intersection against an AABB expanded by rail thickness and rail travel. */
function intersectsRail(box: Box, rail: SegmentSpec): boolean {
  const tx = rail.travelX ?? 0;
  const ty = rail.travelY ?? 0;
  const lows = [box.left - RAIL_HALF_THICKNESS - Math.max(0, tx), box.top - RAIL_HALF_THICKNESS - Math.max(0, ty)];
  const highs = [box.right + RAIL_HALF_THICKNESS - Math.min(0, tx), box.bottom + RAIL_HALF_THICKNESS - Math.min(0, ty)];
  const starts = [rail.ax, rail.ay];
  const deltas = [rail.bx - rail.ax, rail.by - rail.ay];
  let enter = 0;
  let exit = 1;
  for (let axis = 0; axis < 2; axis += 1) {
    const start = starts[axis]!;
    const delta = deltas[axis]!;
    if (delta === 0) {
      if (start < lows[axis]! || start > highs[axis]!) return false;
    } else {
      const a = (lows[axis]! - start) / delta;
      const b = (highs[axis]! - start) / delta;
      enter = Math.max(enter, Math.min(a, b));
      exit = Math.min(exit, Math.max(a, b));
      if (enter > exit) return false;
    }
  }
  return true;
}

function hazardsFor(seed: number, eraSeed: number, index: number, base: number, rails: Rail[]): HazardSpec[] {
  const first = rails[0];
  const second = rails[1];
  if (!first || !second || index < 3) return [];

  const bandBottom = base + CHAMBER_HEIGHT - BAND_BOTTOM_MARGIN;
  const kindCount = index < 6 ? 3 : 5;
  const kind = (["teeth", "gear", "press", "pendulum", "gate"] as const)[pick(seed, index, 41, kindCount)]!;
  const probe = ENEMY_SPECS["produce-apple"];
  let x = first.fromRight ? WALL_LEFT + 20 : WALL_RIGHT - 20 - probe.collisionWidth;
  let y = Math.min(bandBottom - probe.collisionHeight, first.freeY + 26);
  if (kind === "teeth") y = Math.min(bandBottom - probe.collisionHeight, first.freeY + 34);
  else if (kind === "press") y = Math.min(bandBottom - probe.collisionHeight, first.wallY + 44);
  else if (kind === "pendulum") y = (first.freeY + second.wallY) / 2 - probe.collisionHeight / 2;
  else if (kind === "gate") {
    x = second.fromRight ? WALL_LEFT + 14 : WALL_RIGHT - 14 - probe.collisionWidth;
    y = Math.min(bandBottom - probe.collisionHeight, second.wallY - probe.collisionHeight - 18);
  }

  const primary = createEnemy(seed, eraSeed, index, "enemy-a", 46, kind, x, y);
  primary.x = Math.max(WALL_LEFT + 12, Math.min(WALL_RIGHT - primary.width - 12, primary.x));
  const legacy = { x: primary.x, y: primary.y };
  const sign = first.fromRight ? -1 : 1;
  const slope = (first.freeY - first.wallY) / Math.abs(first.freeX - (first.fromRight ? WALL_RIGHT : WALL_LEFT));
  const vx = sign * DEPARTURE_SPEED / Math.sqrt(1 + slope * slope);
  const vy = DEPARTURE_SPEED * slope / Math.sqrt(1 + slope * slope);
  const flight = (Math.sqrt(vy * vy + 2 * 960 * ENEMY_DROP) - vy) / 960;
  const dropX = first.freeX + (first.spec.travelX ?? 0) / 2 + vx * flight;
  const place = (centerX: number, centerY: number): boolean => {
    primary.x = Math.max(WALL_LEFT + 12, Math.min(WALL_RIGHT - primary.width - 12, centerX - primary.width / 2));
    primary.y = centerY - primary.height / 2;
    const envelope = enemyEnvelope(primary);
    return envelope.top >= base + 40 && envelope.bottom <= bandBottom &&
      rails.every((rail) => !intersectsRail(envelope, rail.spec));
  };
  if (!place(dropX, first.freeY + ENEMY_DROP)) {
    // Catch the landing trajectory just above the static lower rail.
    const landingX = lerp(dropX, second.freeX, 0.3);
    const railY = second.spec.ay + (second.spec.by - second.spec.ay) *
      (landingX - second.spec.ax) / (second.spec.bx - second.spec.ax);
    const verticalRange = primary.motion === "vertical" || primary.motion === "adaptive" ? (primary.travelY ?? 0) / 2 : 0;
    if (!place(landingX, railY - RAIL_HALF_THICKNESS - 1 - verticalRange - primary.height / 2)) {
      primary.x = legacy.x;
      primary.y = legacy.y;
    }
  }
  const transition = eraTransitionAt(eraSeed, y);
  const formationChance = lerp(transition.era.formationChance, transition.nextEra.formationChance, transition.progress);
  if (unit(seed, index, 58) >= formationChance) return [primary];
  const formationY = Math.min(bandBottom - 34, y + Math.max(primary.height, 34) + 22);
  if (formationY - y < primary.height + 16) return [primary];
  const formationX = legacy.x < (WALL_LEFT + WALL_RIGHT) / 2 ? WALL_RIGHT - 86 : WALL_LEFT + 38;
  const secondary = createEnemy(seed, eraSeed, index, "enemy-b", 61, kind, formationX, formationY);
  secondary.x = Math.max(WALL_LEFT + 12, Math.min(WALL_RIGHT - secondary.width - 12, secondary.x));
  if (boxesOverlap(enemyEnvelope(primary), enemyEnvelope(secondary))) return [primary];
  return [primary, secondary];
}

function cascadeChamber(seed: number, eraSeed: number, index: number, base: number, era: EraSpec): ChamberLayout {
  const rails = cascade(seed, index, base, era);
  return {
    index,
    label: index < 3 ? "CHRONO INTAKE" : "TIMELINE CASCADE",
    segments: [...walls(index, base), ...rails.map((rail) => rail.spec)],
    hazards: hazardsFor(seed, eraSeed, index, base, rails),
    powerups: powerupsFor(seed, index, base),
  };
}

export function generateChamber(seed: number, index: number, eraSeed = seed): ChamberSpec {
  const base = index * CHAMBER_HEIGHT;
  const era = eraForChamber(eraSeed, index);
  const useSieve = index >= 4 && pick(seed, index, 60, 4) === 0;
  const layout = useSieve ? sieveChamber(seed, index, base, era) : cascadeChamber(seed, eraSeed, index, base, era);
  return { ...layout, era };
}
